/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import {
  listVehicleServiceHistory,
  getVehicleServiceSummary,
  type VehicleServiceHistoryEntry,
  type VehicleServiceSummary,
} from '../infra/vehicleServiceHistoryRepo';
import { plural } from '../../shared/format/plural';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';


import { formatMoney } from '../../shared/format/public-api';
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

type VehicleHistoryRouteParams = {
  vehicleId?: string;
  vehicleLabel?: string;
};

export function VehicleHistoryScreen() {
  const route = useRoute();
  const params = route.params as VehicleHistoryRouteParams | undefined;
  const vehicleId = params?.vehicleId ?? '';
  const vehicleLabel = params?.vehicleLabel ?? 'Vehículo';
  const capabilityGate = useCapabilityGate('work_orders', {
    title: 'Vehículos no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [entries, setEntries] = useState<VehicleServiceHistoryEntry[]>([]);
  const [summary, setSummary] = useState<VehicleServiceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!vehicleId) {
      setError('Falta el ID del vehículo.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [hist, summ] = await Promise.all([
        listVehicleServiceHistory(vehicleId),
        getVehicleServiceSummary(vehicleId),
      ]);
      setEntries(hist);
      setSummary(summ);
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar el historial.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  if (!capabilityGate.allowed) {
    return <ShellContentFrame>{capabilityGate.blocked}</ShellContentFrame>;
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563eb" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={() => void load()}>
          <Text style={styles.retryText}>Reintentar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Historial de servicio</Text>
      <Text style={styles.subtitle}>{vehicleLabel}</Text>

      {summary ? (
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Repuestos</Text>
              <Text style={styles.summaryValue}>{formatMoney(summary.totalParts)}</Text>
              <Text style={styles.summaryCount}>{summary.partsCount} {plural(summary.partsCount, 'ítem', 'ítems')}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Servicios</Text>
              <Text style={styles.summaryValue}>{formatMoney(summary.totalServices)}</Text>
              <Text style={styles.summaryCount}>{summary.servicesCount} {plural(summary.servicesCount, 'ítem', 'ítems')}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Total</Text>
              <Text style={[styles.summaryValue, styles.summaryTotal]}>
                {formatMoney(summary.totalAll)}
              </Text>
              <Text style={styles.summaryCount}>{summary.workOrdersCount} {plural(summary.workOrdersCount, 'OT', 'OTs')}</Text>
            </View>
          </View>
        </View>
      ) : null}

      <Text style={styles.sectionTitle}>Historial detallado</Text>
      {entries.length === 0 ? (
        <Text style={styles.empty}>Sin historial de servicio para este vehículo.</Text>
      ) : (
        entries.map((e, i) => (
          <View
            key={`${e.entryId}-${i}`}
            style={[
              styles.entryCard,
              e.entryType === 'part' ? styles.partCard : styles.taskCard,
            ]}
          >
            <View style={styles.entryHeader}>
              <View style={[styles.typeBadge, e.entryType === 'part' ? styles.partBadge : styles.taskBadge]}>
                <Text style={styles.typeBadgeText}>
                  {e.entryType === 'part' ? 'Repuesto' : 'Servicio'}
                </Text>
              </View>
              <Text style={styles.entryDate}>{formatDate(e.createdAt)}</Text>
            </View>
            <Text style={styles.entryName}>{e.productName || 'Sin nombre'}</Text>
            <Text style={styles.entryDetail}>
              {e.quantity} × {formatMoney(e.unitPrice)} = {formatMoney(e.lineTotal)}
            </Text>
            <Text style={styles.entryOt}>
              OT {e.workOrderId.slice(0, 8).toUpperCase()} · {e.workOrderStatus}
            </Text>
          </View>
        ))
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f9fafb' },
  content: { padding: 16, paddingBottom: 32 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 },
  title: { fontSize: 22, fontWeight: '700', color: '#111827', marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#6b7280', marginBottom: 16 },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between' },
  summaryItem: { alignItems: 'center', flex: 1 },
  summaryLabel: { fontSize: 12, color: '#6b7280', marginBottom: 4 },
  summaryValue: { fontSize: 16, fontWeight: '700', color: '#111827' },
  summaryTotal: { color: '#2563eb', fontSize: 18 },
  summaryCount: { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  sectionTitle: { fontSize: 16, fontWeight: '600', color: '#111827', marginBottom: 8 },
  empty: { textAlign: 'center', color: '#6b7280', fontSize: 14, paddingVertical: 24 },
  entryCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  partCard: { borderLeftWidth: 3, borderLeftColor: '#2563eb' },
  taskCard: { borderLeftWidth: 3, borderLeftColor: '#7c3aed' },
  entryHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  typeBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4 },
  partBadge: { backgroundColor: '#dbeafe' },
  taskBadge: { backgroundColor: '#ede9fe' },
  typeBadgeText: { fontSize: 11, fontWeight: '600' },
  entryDate: { fontSize: 12, color: '#6b7280' },
  entryName: { fontSize: 15, fontWeight: '600', color: '#111827', marginBottom: 2 },
  entryDetail: { fontSize: 13, color: '#374151', marginBottom: 2 },
  entryOt: { fontSize: 12, color: '#9ca3af' },
  errorText: { color: '#dc2626', fontSize: 14, marginBottom: 12, textAlign: 'center' },
  retryButton: { backgroundColor: '#2563eb', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  retryText: { color: '#fff', fontWeight: '600' },
});
